import { useEffect, useRef, useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import { Button, DialogPrompt } from '@campai/lib-web/components/new/ui'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { Page } from '@/components/Page'
import { canDeleteCommunityContent, canModerateCommunityContent } from '@/product/community/access'
import { CategoryDialog } from '@/product/community/home/CategoryDialog'

import { CategoryList } from './CategoryList'

type Category = RouterOutput['product']['community']['categories']['listCategories']['categories'][number]

const CATEGORY_PAGE_SIZE = 50

export const Home = observer(() => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [categories, setCategories] = useState<Category[] | null>(null)
    const [editingCategory, setEditingCategory] = useState<Category | null>(null)
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)
    const [reloadVersion, setReloadVersion] = useState(0)
    const requestVersionRef = useRef(0)
    const canManageCategories = canModerateCommunityContent()
    const canDeleteCategories = canDeleteCommunityContent()

    useEffect(() => {
        const abortController = new AbortController()
        const requestVersion = requestVersionRef.current + 1
        requestVersionRef.current = requestVersion

        setCategories(null)

        void client.product.community.categories.listCategories
            .query(
                {
                    limit: CATEGORY_PAGE_SIZE,
                    offset: 0,
                    returnCount: true,
                    sort: { title: 'asc' },
                },
                { signal: abortController.signal }
            )
            .then((result) => {
                if (requestVersionRef.current === requestVersion) {
                    setCategories(result.categories)
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted && requestVersionRef.current === requestVersion) {
                    setCategories([])
                }
            })

        return () => abortController.abort()
    }, [reloadVersion])

    const reload = () => setReloadVersion((currentVersion) => currentVersion + 1)

    const openCreateDialog = () => {
        setEditingCategory(null)
        setCategoryDialogOpen(true)
    }

    const openEditDialog = (category: Category) => {
        setEditingCategory(category)
        setCategoryDialogOpen(true)
    }

    const openCategory = (category: Category) => {
        void navigate({
            to: '/product/community/categories/$categoryId',
            params: { categoryId: category._id.toString() },
        })
    }

    const handleCategorySubmit = async (input: { title: string; description: string }) => {
        if (editingCategory) {
            await client.product.community.categories.updateCategory.mutate({
                categoryId: editingCategory._id.toString(),
                ...input,
            })
        } else {
            await client.product.community.categories.createCategory.mutate(input)
        }

        reload()
    }

    const deleteCategory = async () => {
        if (!categoryToDelete) {
            return
        }

        await client.product.community.categories.deleteCategory.mutate({
            categoryId: categoryToDelete._id.toString(),
        })
        setCategoryToDelete(null)
        reload()
    }

    return (
        <Page
            title={t('product.community.home.title')}
            description={t('product.community.home.description')}
            className="flex min-h-full flex-col space-y-6 pb-8"
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-muted-foreground text-sm">
                    {t('product.community.home.categories.description')}
                </div>
                {canManageCategories && (
                    <Button onClick={openCreateDialog}>
                        <PlusIcon data-icon="inline-start" />
                        {t('product.community.home.categories.create')}
                    </Button>
                )}
            </div>
            <CategoryList
                categories={categories}
                canManageCategories={canManageCategories}
                canDeleteCategories={canDeleteCategories}
                openCreateDialog={openCreateDialog}
                openEditDialog={openEditDialog}
                openCategory={openCategory}
                setCategoryToDelete={setCategoryToDelete}
            />
            <CategoryDialog
                open={categoryDialogOpen}
                category={editingCategory}
                onOpenChange={setCategoryDialogOpen}
                onSubmit={handleCategorySubmit}
            />
            <DialogPrompt
                open={!!categoryToDelete}
                onOpenChange={(open) => {
                    if (!open) {
                        setCategoryToDelete(null)
                    }
                }}
                title={t('product.community.home.categories.deleteDialog.title')}
                message={t('product.community.home.categories.deleteDialog.message', {
                    title: categoryToDelete?.title || '',
                })}
                confirmLabel={t('product.community.home.categories.deleteDialog.confirm')}
                cancelLabel={t('common.cancel')}
                confirmVariant="destructive"
                onConfirm={deleteCategory}
            />
        </Page>
    )
})
